import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "@/App.css";
import axios from "axios";
import { UploadCloud, FileArchive, Loader2, Download, MessageSquareText,
  ImageIcon, Film, FileText, Users, CircleCheck, TriangleAlert, Settings2, Mic,
  ShieldCheck, Fingerprint, Clock, ChevronDown, Copy, Check,
  Lock, Cpu, ChevronRight, MonitorCheck, Hash, Stamp, X, ServerCog,
  Sun, Moon, Home, MailCheck, BellRing, RefreshCw, Scale, Briefcase } from "lucide-react";

// The backend is hosted separately (Fly) from the frontend (Cloudflare Pages),
// so the API URL always comes from the build-time env; multi-domain access is
// handled by the backend's CORS allow-list.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Ads conversion tracking. The base tag (gtag.js) is loaded via the
// literal snippet in public/index.html; these are public identifiers.
const GADS_ID = "AW-18347530709";
const GADS_LABEL = "MkeVCO2_-dUcENWz5KxE";

function fireAdsConversion(sessionId, amountCents, currency) {
  if (typeof window.gtag !== "function") return;
  const key = `gads_conv_${sessionId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
  } catch { /* storage unavailable — rely on transaction_id dedupe */ }
  const payload = { send_to: `${GADS_ID}/${GADS_LABEL}`, transaction_id: sessionId };
  if (amountCents) { payload.value = amountCents / 100; payload.currency = (currency || "eur").toUpperCase(); }
  window.gtag("event", "conversion", payload);
}

const EU_REGIONS = ["AT", "BE", "CY", "EE", "FI", "FR", "DE", "GR", "IE", "IT",
  "LV", "LT", "LU", "MT", "NL", "PT", "SK", "SI", "ES", "HR"];

// Pick the buyer's likely currency from their browser locale, constrained to the
// currencies we actually support (server-provided). Falls back to the default.
function detectCurrency(supported, fallback) {
  try {
    let region = "";
    try { region = new Intl.Locale(navigator.language).region || ""; } catch { /* older browsers */ }
    if (!region && navigator.language.includes("-")) region = navigator.language.split("-")[1];
    region = (region || "").toUpperCase();
    let cur = fallback;
    if (region === "GB") cur = "gbp";
    else if (region === "US") cur = "usd";
    else if (EU_REGIONS.includes(region)) cur = "eur";
    return supported.includes(cur) ? cur : fallback;
  } catch {
    return fallback;
  }
}

function formatPrice(p) {
  if (!p) return "";
  const amt = p.amount % 1 === 0 ? p.amount.toFixed(0) : p.amount.toFixed(2);
  return `${p.symbol}${amt}`;
}

const PROCESSING_STEPS = [
  "Analysing message structure…",
  "Optimising media assets…",
  "Finalising document security…",
];

const USE_CASES = [
  {
    to: "/legal-court-bundles", icon: Scale, testid: "usecase-legal",
    title: "For Legal Teams",
    body: "Court-admissible exhibits with chain-of-custody verification.",
  },
  {
    to: "/hr-workplace-investigations", icon: Briefcase, testid: "usecase-hr",
    title: "For HR Investigations",
    body: "Confidential, audit-compliant chat logs for internal reviews.",
  },
];

const UseCaseSection = () => (
  <section className="mt-8" data-testid="usecase-section">
    <h2 className="text-base md:text-lg font-bold text-[color:var(--txt)]">
      Designed for Professional Environments.
    </h2>
    <div className="mt-4 grid gap-4 md:grid-cols-2">
      {USE_CASES.map(({ to, icon: Icon, testid, title, body }) => (
        <Link key={to} to={to} data-testid={testid}
          className="group block rounded-xl border border-[color:var(--line)] bg-[color:var(--panel)] p-6 no-underline transition-colors duration-150 hover:border-teal-600/70">
          <span className="grid h-10 w-10 place-items-center rounded-lg border border-[color:var(--line)] bg-[color:var(--panel2)] text-teal-600">
            <Icon size={18} />
          </span>
          <h3 className="mt-4 text-sm font-bold text-[color:var(--txt)]">{title}</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--mut)]">{body}</p>
          <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-teal-600 transition-colors group-hover:text-teal-500">
            Learn More <ChevronRight size={15} />
          </span>
        </Link>
      ))}
    </div>
  </section>
);

const Toggle = ({ checked, onChange, testId }) => (
  <button
    type="button" role="switch" aria-checked={checked} data-testid={testId}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${checked ? "bg-[var(--accent)]" : "bg-[var(--line2)]"}`}
  >
    <span
      className={`pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${checked ? "translate-x-5" : "translate-x-0"}`}
    />
  </button>
);

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="stat-card" data-testid={testid}>
      <Icon size={18} className="stat-ico" />
      <div>
        <div className="stat-val">{value}</div>
        <div className="stat-lbl">{label}</div>
      </div>
    </div>
  );
}

const PRICING_TIERS = [
  {
    key: "basic_export",
    name: "Single Export",
    price: "€29",
    cadence: "one-off",
    icon: FileText,
    desc: "Single-use credit to convert one WhatsApp conversation archive into a formatted PDF record.",
    features: [
      "1 conversation → court-ready PDF",
      "SHA-256 cryptographic verification",
      "Offline voice-note transcription"
    ],
    available: true,
  },
  {
    key: "professional_plan",
    name: "Professional Plan",
    price: "Soon",
    cadence: "monthly",
    icon: FileArchive,
    desc: "Ideal for small law firms requiring recurring evidence processing for their team.",
    features: [
      "Up to 10 user licences",
      "Bundle Verification Dashboard",
      "Priority processing queue",
      "Priority email support"
    ],
    available: false,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "Soon",
    cadence: "monthly",
    icon: Users,
    desc: "Tailored processing volume and strict compliance support for large law firms and IT departments.",
    features: [
      "Unlimited user licences",
      "Bundle Verification Dashboard",
      "Vendor Security Review support",
      "Dedicated Account Manager"
    ],
    available: false,
  },
];

function NotifyForm({ planKey }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { setState("error"); setMsg("Enter a valid email."); return; }
    setState("sending"); setMsg("");
    try {
      const res = await axios.post(`${API}/waitlist`, { email, plan: planKey });
      setState("done");
      setMsg(res.data?.message || "You're on the list — we'll email you at launch.");
    } catch (err) {
      setState("error");
      setMsg(err?.response?.data?.detail || "Could not sign you up. Please try again.");
    }
  };

  if (state === "done") {
    return (
      <div className="notify-done" data-testid={`waitlist-success-${planKey}`}>
        <MailCheck size={16} /> <span>{msg}</span>
      </div>
    );
  }
  return (
    <form className="notify-form" onSubmit={submit} data-testid={`waitlist-form-${planKey}`}>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="Notify me when available" aria-label="Email for waitlist"
        data-testid={`waitlist-email-${planKey}`} />
      <button type="submit" disabled={state === "sending"} data-testid={`waitlist-submit-${planKey}`}>
        {state === "sending" ? <Loader2 size={15} className="spin" /> : <BellRing size={15} />}
        Notify me
      </button>
      {state === "error" && <span className="notify-err" data-testid={`waitlist-error-${planKey}`}>{msg}</span>}
    </form>
  );
}

function PricingTiers({ onSelectBasic, flash }) {
  return (
    <section className="pricing-wrap" id="plans" data-testid="pricing-section">
      <div className="pricing-head">
        <span className="eyebrow">// PLANS</span>
        <h2 className="pricing-title">Simple pricing, built to scale</h2>
        <p className="pricing-sub">Start with a single export today. Bulk & enterprise archiving are on the way.</p>
      </div>
      <div className="pricing-grid">
        {PRICING_TIERS.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.key}
              className={`tier-card${t.available ? "" : " tier-disabled"}`}
              data-testid={`tier-${t.key}`}>
              {!t.available && (
                <span className="soon-badge" data-testid={`soon-badge-${t.key}`}>Coming Soon</span>
              )}
              <div className="tier-icon"><Icon size={20} /></div>
              <h3 className="tier-name">{t.name}</h3>
              <div className="tier-price">
                {t.available ? (
                  <><span className="tier-amount">{t.price}</span><span className="tier-cadence"> / {t.cadence}</span></>
                ) : (
                  <span className="tier-cadence">Monthly subscription</span>
                )}
              </div>
              <p className="tier-desc">{t.desc}</p>
              <ul className="tier-features">
                {t.features.map((f) => (
                  <li key={f}><Check size={14} /> {f}</li>
                ))}
              </ul>
              {t.available ? (
                <button className="tier-cta" onClick={onSelectBasic} data-testid={`tier-cta-${t.key}`}>
                  <UploadCloud size={16} /> Start an export
                </button>
              ) : (
                <div className={`notify-wrap${flash ? " notify-flash" : ""}`}>
                  <NotifyForm planKey={t.key} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function App() {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | analyzing | analyzed | success
  const [summary, setSummary] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [email, setEmail] = useState(() => localStorage.getItem("deliveryEmail") || "");
  const [paying, setPaying] = useState(false);
  const [payMsg, setPayMsg] = useState("");
  const [successTitle, setSuccessTitle] = useState("Payment received");
  const [statusIdx, setStatusIdx] = useState(0);
  const [renderStage, setRenderStage] = useState("");
  const [showSolutions, setShowSolutions] = useState(false);

  // Cycle the reassuring processing-status messages while an upload is analysed.
  useEffect(() => {
    if (!loading) { setStatusIdx(0); return; }
    const t = setInterval(
      () => setStatusIdx((i) => (i + 1) % PROCESSING_STEPS.length), 2600);
    return () => clearInterval(t);
  }, [loading]);
  const [prices, setPrices] = useState([]);
  const [currency, setCurrency] = useState("gbp");
  const [pricingMode, setPricingMode] = useState("inline");
  const [priceDisplay, setPriceDisplay] = useState(null);
  const [regenEligible, setRegenEligible] = useState(false);
  const [regenChecking, setRegenChecking] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState("");
  const [showOpts, setShowOpts] = useState(false);
  const [showAdv, setShowAdv] = useState(false);
  const [dateOrder, setDateOrder] = useState("auto");
  const [quality, setQuality] = useState(68);
  const [title, setTitle] = useState("");
  const [transcribe, setTranscribe] = useState(true);
  const [caseRef, setCaseRef] = useState("");
  const [affidavit, setAffidavit] = useState(true);
  const [participants, setParticipants] = useState(true);
  const [showCompliance, setShowCompliance] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  const [notifyFlash, setNotifyFlash] = useState(false);

  useEffect(() => {
    if (window.location.hash !== "#plans") return;
    const t1 = setTimeout(() => {
      window.history.replaceState({}, "", "/");
      document.getElementById("plans")?.scrollIntoView({ behavior: "smooth", block: "start" });
      setNotifyFlash(true);
    }, 150);
    const t2 = setTimeout(() => setNotifyFlash(false), 4500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const startExport = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => inputRef.current?.click(), 350);
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopPolling(), []);

  const COMPLETED_MSG = "Please check your inbox (and spam folder) for the delivery email. For your security, the download link will expire 1 hour from the time of delivery.";

  // Poll the render job until the PDF is built + emailed, then flip the UI to
  // the "email sent" confirmation. Used by both the Stripe-return flow and the
  // free re-generation flow.
  const pollJobCompletion = (jid) => {
    if (!jid) return;
    stopPolling();
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const js = await axios.get(`${API}/status/${jid}`);
        if (js.data.status === "completed") {
          stopPolling();
          setRenderStage("");
          setEmailSent(true);
          return;
        }
        if (js.data.status === "failed") {
          stopPolling();
          setRenderStage("");
          setError("Generation failed after payment. Please contact support with your reference.");
          return;
        }
        if (js.data.status === "rendering" && js.data.stage) {
          setRenderStage(js.data.stage);
        }
      } catch (_) {
        // Job may already have been purged after delivery — keep prior message.
      }
      if (tries >= 400) { // ~20 min cap: very large exports keep working server-side
        stopPolling();
        setRenderStage("");
        setPayMsg("Please note: Comprehensive processing typically takes 2 to 5 minutes. You can safely close this tab, delivery continues on our secure servers, and your fresh download link will arrive via email shortly.");
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  // Handle the return redirect from Stripe Checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sid = params.get("session_id");
    if (payment === "success" && sid) {
      window.history.replaceState({}, "", window.location.pathname);
      setPhase("success");
      setPayMsg("Verifying your payment…");
      window.scrollTo({ top: 0 });
      let attempts = 0;
      const paidMsg = "Payment received! We are rendering your PDF. You will receive a secure download link via email shortly.";
      const checkOnce = async () => {
        attempts += 1;
        try {
          const st = await axios.get(`${API}/checkout/status/${sid}`);
          const jid = st.data.job_id;
          if (jid) setJobId(jid);
          if (st.data.status === "expired") {
            stopPolling();
            setError("Your payment session expired. Please try again.");
            setPhase("idle");
            return;
          }
          if (st.data.payment_status === "paid" || st.data.status === "complete") {
            fireAdsConversion(sid, st.data.amount_total, st.data.currency);
            setPayMsg(paidMsg);
            // Payment confirmed — hand off to the job-completion watcher so the
            // screen auto-updates to "email sent" once the PDF is delivered.
            if (jid) {
              pollJobCompletion(jid);
              return;
            }
          }
        } catch (e) {
          console.error("checkout status poll failed", e);
        }
        if (attempts >= 100) { // ~5 min hard cap
          stopPolling();
          setPayMsg("Payment is being processed. You will receive your secure download link by email shortly. You can safely close this tab.");
        }
      };
      checkOnce(); // fetch immediately, don't wait 3s
      pollRef.current = setInterval(checkOnce, 3000);
    } else if (payment === "cancel") {
      window.history.replaceState({}, "", window.location.pathname);
      setError("Payment was cancelled. Your file was not processed. Please upload again to retry.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      setError("Please select the WhatsApp export as a .zip file.");
      return;
    }
    setError("");
    setFile(f);
    // Conversion nudge: reveal the cover-page fields as soon as a file is ready.
    setShowOpts(true);
    if (!localStorage.getItem("hasSeenNudge")) setShowNudge(true);
  };

  const dismissNudge = () => {
    setShowNudge(false);
    localStorage.setItem("hasSeenNudge", "true");
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    pick(e.dataTransfer.files?.[0]);
  }, []);

  const reset = () => {
    stopPolling();
    setPhase("idle");
    setSummary(null);
    setJobId(null);
    setFile(null);
    setError("");
    setPayMsg("");
    setSuccessTitle("Payment received");
    setRenderStage("");
    setEmailSent(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Fast hook: upload + metadata extraction only (no PDF yet).
  const analyze = async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    setPhase("analyzing");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("date_order", dateOrder);
      fd.append("quality", String(quality));
      fd.append("title", title);
      fd.append("transcribe", String(transcribe));
      fd.append("case_ref", caseRef);
      fd.append("affidavit", String(affidavit));
      fd.append("participants", String(participants));
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const id = res.data.job_id;
      setJobId(id);
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/status/${id}`);
          if (st.data.status === "analyzed") {
            stopPolling();
            setSummary({ ...st.data.summary, price_gbp: st.data.price_gbp });
            if (st.data.pricing_mode === "stripe_price") {
              setPricingMode("stripe_price");
              setPriceDisplay(st.data.price_display || null);
            } else {
              setPricingMode("inline");
              const list = st.data.prices || [];
              setPrices(list);
              setPriceDisplay(st.data.price_display || null);
              setCurrency(detectCurrency(list.map((p) => p.currency), st.data.default_currency || "eur"));
            }
            setPhase("analyzed");
            setLoading(false);
            // Returning customer? auto-detect prior purchase of this exact file.
            checkRegen(id, email);
          } else if (st.data.status === "failed") {
            stopPolling();
            setError(st.data.error || "We couldn't read that export. Please check the file.");
            setPhase("idle");
            setLoading(false);
          }
        } catch (err) {
          stopPolling();
          setError("Lost connection while analyzing. Please try again.");
          setPhase("idle");
          setLoading(false);
        }
      }, 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed. Check the file and try again.");
      setPhase("idle");
      setLoading(false);
    }
  };

  // Persist the delivery email and auto-detect a prior purchase of this exact
  // file so returning customers see a free "Regenerate" action, not a paywall.
  const onEmailChange = (val) => {
    setEmail(val);
    if (val && val.includes("@")) localStorage.setItem("deliveryEmail", val);
  };

  const checkRegen = useCallback(async (jid, mail) => {
    if (!jid || !mail || !mail.includes("@")) { setRegenEligible(false); return; }
    setRegenChecking(true);
    try {
      const res = await axios.post(`${API}/regeneration-check`, { job_id: jid, email: mail });
      setRegenEligible(!!res.data?.eligible);
    } catch {
      setRegenEligible(false);
    } finally {
      setRegenChecking(false);
    }
  }, []);

  // 'Recover Purchase' (Zero-Retention safe): the re-uploaded file's SHA-256
  // matched a prior paid record, so regenerate free of charge — no Stripe.
  const recover = async () => {
    if (!jobId) return;
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address so we can deliver your PDF.");
      return;
    }
    setPaying(true);
    setError("");
    try {
const res = await axios.post(`${API}/recover`, { job_id: jobId, email });
setPaying(false);
setPhase("success");
setEmailSent(false);
setSuccessTitle("Purchase recovered");
setPayMsg(
  <div className="flex flex-col gap-3">
    <span>Purchase verified. Recovering your court-ready bundle free of charge.A fresh secure download link (valid 1 hour) is on its way to your email. </span>
    <em className="italic text-[color:var(--mut)]">Please note: Comprehensive processing of multi-year archives may take 5 to 10 minutes to fully render and encrypt.</em>
  </div>
);
window.scrollTo({ top: 0, behavior: "smooth" });
pollJobCompletion(res.data.job_id || jobId);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not verify your previous purchase. Please try again.");
      setPaying(false);
    }
  };

  // Paywall: create a Stripe Checkout session and redirect.
  const purchase = async () => {
    if (!jobId) return;
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address so we can deliver your PDF.");
      return;
    }
    setPaying(true);
    setError("");
    try {
      const res = await axios.post(`${API}/checkout`, {
        job_id: jobId,
        origin_url: window.location.origin,
        email,
        currency,
      });
      if (res.data.regeneration) {
        // Prior purchase of this exact file detected — skip Stripe, regenerate free.
        setPaying(false);
        setPhase("success");
        setEmailSent(false);
        
        setPayMsg(
          <span>
            Verified prior purchase for this file. Regenerating your court ready bundle free of charge. A fresh secure download link is on its way to your email. <em className="italic text-[color:var(--mut)]">Please note: Comprehensive processing of multi-year archives may take 5 to 10 minutes to fully render and encrypt.</em>
          </span>
        );
        
        window.scrollTo({ top: 0, behavior: "smooth" });
        pollJobCompletion(res.data.job_id || jobId);
        return;
      }
      window.location.href = res.data.url; // redirect to Stripe Checkout
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setPaying(false);
    }
  };

  return (
    <div className="page">
      <Helmet>
        <title>WhatsApp to PDF for Legal & HR Investigations | AuditPDF</title>
        <meta
          name="description"
          content="Convert WhatsApp .txt exports into secure, court-ready PDF productions. 100% offline, deterministic processing with zero data retention."
        />
        <link rel="canonical" href="https://auditpdfhq.com/" />
      </Helmet>
      <header className="topbar" data-testid="app-header">
        <div className="brand brand-clickable" data-testid="brand-home"
          role="button" tabIndex={0} onClick={reset}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); reset(); } }}
          title="Back to home">
          <div className="brand-logo"><ShieldCheck size={22} /></div>
          <div>
            <div className="brand-name" data-testid="brand-title">AuditPDF<span className="brand-dot">.</span></div>
            <p className="brand-tag">Verifiable Chat Production Engine</p>
          </div>
        </div>
        <div className="topbar-right">      
          <div className="trust-badges">
            <span className="tbadge"><Lock size={13} /> 100% Offline</span>
            <span className="tbadge"><Cpu size={13} /> Deterministic</span>
            <span className="tbadge"><ShieldCheck size={13} /> Zero-Retention</span>
          </div>
          <button type="button" className="theme-toggle" data-testid="theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>

      {(phase === "idle" || phase === "analyzing") && (
      <section className="hero" data-testid="hero">
        <div className="hero-copy">
          <span className="eyebrow">// SECURE WHATSAPP PROCESSING FOR LITIGATION & PRE-DISCOVERY</span>
          <h1 data-testid="page-h1">WhatsApp to PDF for Legal & HR Investigations.</h1>
          <h2>Turn a WhatsApp .txt export into a<br /><span className="grad"> secure court-ready PDF bundle</span>.</h2>
          <p>Turn standard WhatsApp .txt exports into audit-compliant document bundles. Processed entirely in temporary memory with absolute zero data retention.</p>
          <div className="pipeline">
            <div className="pstep"><span className="pnum">01</span><div><b>Structured Parsing</b><small>High-fidelity data extraction</small></div></div>
            <ChevronRight size={16} className="parrow" />
            <div className="pstep"><span className="pnum">02</span><div><b>Transcribe</b><small>Secure, offline voice processing</small></div></div>
            <ChevronRight size={16} className="parrow" />
            <div className="pstep"><span className="pnum">03</span><div><b>Render Bundle</b><small>SHA-256 hashed PDF & media</small></div></div>
          </div>
        </div>
      </section>
      )}

      <main className="wrap">
        {(phase === "idle" || phase === "analyzing") && (
        <>
        <section className="card upload-card">
          <div
            className={`dropzone ${dragging ? "drag" : ""} ${file ? "has-file" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="dropzone"
          >
            <input
              ref={inputRef} type="file" accept=".zip" hidden
              data-testid="file-input"
              onChange={(e) => pick(e.target.files?.[0])}
            />
            {file ? (
              <div className="file-chosen" data-testid="file-chosen">
                <FileArchive size={40} />
                <div>
                  <div className="fname">{file.name}</div>
                  <div className="fsize">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                </div>
              </div>
            ) : (
              <>
                <UploadCloud size={46} className="up-ico" />
                <p className="dz-title">Drop your WhatsApp export .zip here</p>
                <p className="dz-sub">or click to browse — must contain <code>_chat.txt</code> and media</p>
              </>
            )}
          </div>

          <button className="opts-toggle" onClick={() => setShowOpts(!showOpts)} data-testid="options-toggle">
            <Settings2 size={16} /> {showOpts ? "Hide options" : "Options"}
          </button>
          {showOpts && (
            <div className="opts grid grid-cols-1 gap-y-8 md:grid-cols-2 md:gap-x-12" data-testid="options-panel">
              <div className="flex w-full flex-col gap-1.5 text-left" data-testid="field-title">
                <span className="opt-label">Title (chat header)</span>
                <input className="w-full" value={title} data-testid="opt-title"
                  onChange={(e) => setTitle(e.target.value)} placeholder="Auto (folder name)" />
              </div>
              <div className="nudge-anchor flex w-full flex-col gap-1.5 text-left" data-testid="field-case-ref">
                <span className="opt-label">Case / Matter Reference (cover page)</span>
                <input className="w-full" value={caseRef} data-testid="opt-case-ref"
                  onChange={(e) => setCaseRef(e.target.value)}
                  onFocus={() => { if (showNudge) dismissNudge(); }}
                  placeholder="e.g. CASE-2026-00123" />
                {showNudge && (
                  <div className="opt-nudge" data-testid="case-nudge" role="note">
                    <button type="button" className="opt-nudge-close" aria-label="Dismiss tip"
                      data-testid="case-nudge-close" onClick={dismissNudge}>
                      <X size={13} />
                    </button>
                    <span className="opt-nudge-badge">Tip</span>
                    Enter your Case Number and Parties here to have them included on your
                    Court-Ready cover page.
                  </div>
                )}
              </div>
              <div className="flex w-full items-center justify-between gap-3" data-testid="field-participants">
                <span className="opt-label">Include Schedule of Participants</span>
                <Toggle checked={participants} onChange={setParticipants} testId="opt-participants" />
              </div>
              <div className="flex w-full items-center justify-between gap-3" data-testid="field-affidavit">
                <span className="opt-label">Append "Affidavit of Authenticity"</span>
                <Toggle checked={affidavit} onChange={setAffidavit} testId="opt-affidavit" />
              </div>
              <div className="adv-block col-span-1 w-full border-t border-[var(--line)] pt-4 md:col-span-2">
                <button type="button" className="adv-toggle" data-testid="advanced-toggle"
                  onClick={() => setShowAdv((s) => !s)} aria-expanded={showAdv}>
                  <ChevronDown size={14} className={showAdv ? "rot" : ""} /> Advanced Settings
                </button>
                {showAdv && (
                  <div className="mt-5 grid w-full grid-cols-1 gap-y-8 md:grid-cols-2 md:gap-x-12" data-testid="advanced-panel">
                    <div className="flex w-full flex-col gap-1.5 text-left" data-testid="field-date-order">
                      <span className="opt-label">Date order</span>
                      <select className="w-full" value={dateOrder} onChange={(e) => setDateOrder(e.target.value)} data-testid="opt-date-order">
                        <option value="auto">Auto-detect</option>
                        <option value="mdy">US (M/D/Y)</option>
                        <option value="dmy">Intl (D/M/Y)</option>
                      </select>
                    </div>
                    <div className="flex w-full flex-col gap-1.5 text-left" data-testid="field-quality">
                      <span className="opt-label">Image quality: {quality}%</span>
                      <input className="w-full" type="range" min="40" max="95" value={quality} data-testid="opt-quality"
                        onChange={(e) => setQuality(Number(e.target.value))} />
                    </div>
                    <div className="flex w-full items-center justify-between gap-3" data-testid="field-transcribe">
                      <span className="opt-label">Transcribe voice notes</span>
                      <Toggle checked={transcribe} onChange={setTranscribe} testId="opt-transcribe" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && <div className="error" data-testid="error-msg"><TriangleAlert size={16} /> {error}</div>}

          <button className="convert-btn" onClick={analyze} disabled={!file || loading} data-testid="convert-btn">
            {loading ? (<><Loader2 size={18} className="spin" /> Analysing…</>)
                     : (<>Analyse Chat</>)}
          </button>
          {loading && (
            <>
              <p className="processing-status" data-testid="processing-status" aria-live="polite">
                <span key={statusIdx} className="status-step">{PROCESSING_STEPS[statusIdx]}</span>
              </p>
              <a className="why-link" data-testid="why-processing-link"
                 href="/faq#processing" target="_blank" rel="noopener noreferrer">
                Why does processing take a while?
              </a>
            </>
          )}
          <p className="recover-hint" data-testid="recover-hint">
            <RefreshCw size={13} />
            <span>Missed your 60-minute download window? Re-upload the exact
            same export; we verify your previous purchase by its SHA-256 fingerprint and
            regenerate your bundle <b>free of charge</b>.</span>
          </p>
        </section>

        <section className="card compliance-card" data-testid="compliance-card">
          <button className="compliance-toggle" data-testid="compliance-toggle"
            onClick={() => setShowCompliance(!showCompliance)}>
            <span className="ct-title"><ShieldCheck size={18} /> Security &amp; Compliance Framework</span>
            <ChevronDown size={18} className={`chev ${showCompliance ? "open" : ""}`} />
          </button>
          {showCompliance && (
            <div className="compliance-body" data-testid="compliance-body">
              <div className="comp-item">
                <h4>1. Zero-Retention Architecture</h4>
                <p>AuditPDF operates on a strictly ephemeral processing pipeline. Uploaded files are processed in isolated, temporary environments and are never stored in persistent databases. Upon successful download, or upon reaching the strict 60-minute retention limit, all source data and generated files are permanently destroyed. No backups, cached copies, or hidden logs of your data are maintained.</p>
              </div>
              <div className="comp-item">
                <h4>2. Zero Third-Party AI Exposure</h4>
                <p>Unlike standard cloud-based text processors, this tool utilizes deterministic regex extraction algorithms and entirely offline, localized voice transcription models. Your sensitive case data, trade secrets, and eDiscovery materials are never exposed to third-party AI APIs and are never used for machine learning model training.</p>
              </div>
              <div className="comp-item">
                <h4>3. Visual Authenticity Preservation</h4>
                <p>The output formatting strictly separates user-generated messages from system-generated metadata. Original attachment filenames, exact UTC/local timestamps, and media file references are explicitly maintained to ensure the generated PDF serves as an uncompromised, court-ready secondary record.</p>
              </div>
              <div className="comp-item">
                <h4>4. Format Integrity & Source Limitations</h4>
                <p>AuditPDF generates a SHA-256 hash of the provided export file at the moment of processing, creating a verifiable cryptographic link between the provided data and the generated PDF. However, AuditPDF does not perform direct device extraction. The software assumes the user has not tampered with the native WhatsApp .txt export prior to upload. For strict legal admissibility, users are advised to establish chain-of-custody for the source file in accordance with local jurisdictional protocols.</p>
              </div>
            </div>
          )}
        </section>
        <UseCaseSection />
        {phase === "idle" && <PricingTiers onSelectBasic={startExport} flash={notifyFlash} />}
        </>
        )}

        {phase === "analyzed" && summary && (
          <section className="card result-card" data-testid="summary-card">
            <div className="result-head">
              <div className="result-head-left">
                <CircleCheck size={22} className="ok-ico" />
                <h2>Success — your chat is ready to compile</h2>
              </div>
              <div className="result-actions">
                <button className="new-conv-btn" onClick={reset} data-testid="new-conversion-btn">
                  <UploadCloud size={16} /> Start over
                </button>
              </div>
            </div>

            <p className="summary-line" data-testid="summary-line">
              We found <b>{summary.total_messages?.toLocaleString()}</b> messages
              {summary.start_date && summary.end_date ? (
                <> between <b>{summary.start_date}</b> and <b>{summary.end_date}</b></>
              ) : null}
              {summary.media_count ? <>, including <b>{summary.media_count?.toLocaleString()}</b> media items</> : null}.
              Ready to compile your audit-compliant document bundles..
            </p>

            <div className="stats-grid">
              <Stat icon={MessageSquareText} label="Messages" value={summary.total_messages?.toLocaleString()} testid="stat-messages" />
              <Stat icon={ImageIcon} label="Media items" value={summary.media_count?.toLocaleString()} testid="stat-media" />
              <Stat icon={Clock} label="From" value={summary.start_date || "—"} testid="stat-start" />
              <Stat icon={Clock} label="To" value={summary.end_date || "—"} testid="stat-end" />
            </div>

            {error && <div className="error" data-testid="error-msg"><TriangleAlert size={16} /> {error}</div>}

            <div className="paywall" data-testid="paywall">
              <label className="email-field">
                <span>Delivery email (your secure download link is sent here)</span>
                <input type="email" value={email} data-testid="email-input"
                  onChange={(e) => onEmailChange(e.target.value)}
                  onBlur={(e) => checkRegen(jobId, e.target.value)}
                  placeholder="you@example.com" />
              </label>
              {regenEligible ? (
                <>
                  <div className="regen-note" data-testid="regen-note">
                    <CircleCheck size={18} />
                    <span>We found a previous purchase for this exact file under this email.
                      You can recover your court ready bundle <b>free of charge</b>.</span>
                  </div>
                  <button className="download-btn" onClick={recover} disabled={paying} data-testid="regenerate-btn">
                    {paying ? (<><Loader2 size={18} className="spin" /> Recovering…</>)
                            : (<><Download size={18} /> Recover purchase - regenerate free</>)}
                  </button>
                </>
              ) : (
                <>
                  <button className="download-btn" onClick={purchase} disabled={paying || regenChecking} data-testid="purchase-btn">
                    {paying ? (<><Loader2 size={18} className="spin" /> Redirecting to secure checkout…</>)
                            : (<><Lock size={18} /> Purchase &amp; Generate ({formatPrice(priceDisplay) || "Secure Checkout"})</>)}
                  </button>
                  {pricingMode === "stripe_price" && (
                    <p className="bundle-note" data-testid="local-currency-note" style={{ marginTop: 6 }}>
                      Price shown in your local currency at checkout.
                    </p>
                  )}
                </>
              )}
              <p className="bundle-note" data-testid="paywall-note">
                {regenEligible ? (
                  <>Your court ready bundle will be re-rendered and a fresh one-time download link
                    (valid 1 hour) emailed to you. Zero-Retention: the file is purged the moment you download it.</>
                ) : (
                  <>Secure payment via <b>Stripe</b>. After payment, your secure audit-compliant PDF is rendered and a
                    one-time download link (valid 1 hour) is emailed to you. Zero-Retention: the file is
                    purged the moment you download it.</>
                )}
              </p>
            </div>
          </section>
        )}

        {phase === "success" && (
          <section className="card result-card" data-testid="success-card">
            <div className="result-head">
              <div className="result-head-left">
                <CircleCheck size={22} className="ok-ico" />
                <h2>{successTitle}</h2>
              </div>
              <div className="result-actions">
                <button className="ghost-btn" onClick={reset} data-testid="home-btn">
                  <Home size={16} /> Home
                </button>
                <button className="new-conv-btn" onClick={reset} data-testid="new-conversion-btn">
                  <UploadCloud size={16} /> New conversion
                </button>
              </div>
            </div>
            {!emailSent && (
              <div className="purged-note" data-testid="success-message">
                <CircleCheck size={18} />
                <span>{payMsg || "Payment received! We are rendering your PDF. You will receive a secure download link via email shortly."}</span>
              </div>
            )}
            {!emailSent && renderStage && (
              <p className="stage-line" data-testid="render-stage">
                <Loader2 size={13} className="spin" /> {renderStage}
              </p>
            )}
            {emailSent && (
  <div className="email-sent-note" data-testid="email-sent-note" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
    <MailCheck size={20} style={{ marginTop: '2px', flexShrink: 0 }} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <strong>Your secure download link has been successfully dispatched.</strong>
      <span style={{ fontSize: '0.9em', opacity: 0.9 }}>
        {COMPLETED_MSG}
      </span>
    </div>
  </div>
)}
          </section>
        )}
      </main>


      <footer className="w-full mt-12 border-t border-[color:var(--line)] py-10 px-6" data-testid="app-footer">
  <div className="max-w-3xl mx-auto flex flex-col items-center text-center gap-6">
    <div className="flex flex-wrap justify-center gap-6 mt-2" data-testid="footer-trust-bar">
        {[
          { id: "local-first", Icon: MonitorCheck, tip: "Privacy-First Processing" },
          { id: "sha256", Icon: Hash, tip: "Cryptographic Integrity" },
          { id: "gdpr", Icon: ShieldCheck, tip: "GDPR Compliant" },
          { id: "court-ready", Icon: Stamp, tip: "Production-Ready Output" },
          { id: "soc2", Icon: ServerCog, tip: "SOC 2 Infrastructure" },
        ].map(({ id, Icon, tip }) => (
          <span key={id} className="trust-icon relative group flex items-center" data-testid={`trust-${id}`}>
            <Icon size={20} strokeWidth={1.75} color="#008080" aria-hidden="true" />
            {/* The Tooltip logic restored */}
            <span className="trust-tip absolute bottom-full mb-2 hidden group-hover:block bg-black text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap" role="tooltip">
              {tip}
            </span>
          </span>
        ))}
      </div>
    {/* Row 1: Brand & Tagline */}
    <div className="space-y-3">
      <p className="text-sm font-medium text-[color:var(--txt)]" data-testid="footer-tagline">
        AuditPDF | Zero-Retention Processing | Cryptographic Integrity
      </p>
      
      {/* Trust Bar — Restored with full Tooltip Functionality */}
      
    </div>

    {/* Row 2: Nav Links */}
    <nav className="flex items-center gap-3 text-sm text-[color:var(--mut)]" data-testid="footer-links">
      <Link to="/security" className="hover:text-[color:var(--txt)] transition-colors">Security</Link>
      <span>·</span>
      <Link to="/faq" className="hover:text-[color:var(--txt)] transition-colors">FAQ</Link>
      <span>·</span>
      <Link to="/hr-workplace-investigations" className="hover:text-[color:var(--txt)] transition-colors" data-testid="footer-hr">For HR Teams</Link>
      <span>·</span>
      <Link to="/legal-court-bundles" className="hover:text-[color:var(--txt)] transition-colors" data-testid="footer-legal">For Legal Teams</Link>
      <span>·</span>
      <Link to="/verify" className="text-teal-600 font-medium hover:text-teal-800 transition-colors">Verify a Bundle</Link>
    </nav>

    <nav className="flex flex-col items-center gap-2 text-sm" data-testid="footer-use-cases" aria-label="Use Cases">
      <p className="text-xs font-semibold uppercase tracking-wide text-[color:var(--mut2)]">Use Cases</p>
      <div className="flex items-center gap-3 text-[color:var(--mut)]">
        <Link to="/whatsapp-evidence-divorce-family-court" className="hover:text-[color:var(--txt)] transition-colors" data-testid="footer-family">Family Court</Link>
        <span>·</span>
        <Link to="/whatsapp-evidence-tenancy-disputes" className="hover:text-[color:var(--txt)] transition-colors" data-testid="footer-tenancy">Tenancy</Link>
      </div>
    </nav>

    {/* Row 3: Legal Links & Disclaimer Button */}
    <div className="flex flex-wrap justify-center items-center gap-2 text-xs text-[color:var(--mut)]">
      <Link to="/privacy" className="hover:text-[color:var(--txt)] transition-colors">Privacy</Link>
      <span>·</span>
      <Link to="/terms" className="hover:text-[color:var(--txt)] transition-colors">Terms</Link>
      <span>·</span>
      <Link to="/refunds" className="hover:text-[color:var(--txt)] transition-colors">Refunds</Link>
      <span>·</span>
      <button 
        type="button" 
        onClick={() => setShowDisclaimer(true)}
        className="hover:text-[color:var(--txt)] transition-colors underline decoration-dotted underline-offset-4"
      >
        Legal Disclaimer
      </button>
    </div>

    {/* Row 4: Copyright */}
    <p className="text-xs text-[color:var(--mut2)]" data-testid="footer-copyright">
      © 2026 AuditPDF, a product of Accurestack Systems Ltd. All rights reserved.
    </p>
  </div>
</footer>

      {showDisclaimer && (
        <div className="modal-overlay" data-testid="disclaimer-overlay"
          onClick={() => setShowDisclaimer(false)}>
          <div className="modal" data-testid="disclaimer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 data-testid="disclaimer-title">Legal Disclaimer</h3>
              <button type="button" className="modal-close" data-testid="disclaimer-close"
                aria-label="Close" onClick={() => setShowDisclaimer(false)}>×</button>
            </div>
            <p className="modal-body" data-testid="disclaimer-text">
              AuditPDF is a data processing and production engine. It is not a forensic
              acquisition tool. Admissibility of evidence is subject to the rules of court
              and the discretion of the presiding authority. Users are responsible for
              verifying the integrity of transcripts against source media.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
